import React, { useState } from 'react';
import { LegalDocumentModal } from './LegalDocumentModal';
import { LegalDocKind, PRIVACY_POLICY_URL } from './legalDocuments';

/*
 * Два ОТДЕЛЬНЫХ чекбокса для форм, где пользователь оставляет свои данные
 * (регистрация, открытая регистрация, заявка на доступ):
 *   1) согласие на обработку ПДн + ознакомление с Политикой — обязательное;
 *   2) согласие на рекламные рассылки — необязательное (показывается, если
 *      передан onMarketingChange).
 * Оба не отмечены по умолчанию. Тексты согласий открываются в модалке,
 * Политика — PDF на fmeter.ru в новой вкладке.
 */

interface Props {
  personalData: boolean;
  onPersonalDataChange: (value: boolean) => void;
  marketing?: boolean;
  onMarketingChange?: (value: boolean) => void;
  /** Текст ошибки под обязательным чекбоксом (из формы). */
  error?: string;
  className?: string;
}

const linkCls =
  'text-[#e3282a] underline underline-offset-2 hover:text-[#c82022] bg-transparent border-0 p-0 cursor-pointer [font:inherit]';

const Row: React.FC<{
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  invalid?: boolean;
  children: React.ReactNode;
}> = ({ id, checked, onChange, invalid, children }) => (
  <div className="flex items-start gap-3">
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      aria-invalid={invalid || undefined}
      className={`mt-[2px] h-5 w-5 shrink-0 cursor-pointer accent-[#e3282a] ${
        invalid ? 'outline outline-1 outline-[#e3282a] rounded-sm' : ''
      }`}
    />
    <label htmlFor={id} className="text-sm leading-[150%] text-[#2d2c2c] cursor-pointer">
      {children}
    </label>
  </div>
);

export const ConsentCheckboxes: React.FC<Props> = ({
  personalData,
  onPersonalDataChange,
  marketing = false,
  onMarketingChange,
  error,
  className = '',
}) => {
  const [openDoc, setOpenDoc] = useState<LegalDocKind | null>(null);

  // Клик по ссылке внутри <label> не должен переключать чекбокс.
  const open = (kind: LegalDocKind) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDoc(kind);
  };

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <Row
        id="consent_personal_data"
        checked={personalData}
        onChange={onPersonalDataChange}
        invalid={Boolean(error)}
      >
        Я даю{' '}
        <button type="button" className={linkCls} onClick={open('personal-data')}>
          согласие на обработку персональных данных
        </button>{' '}
        и ознакомлен(а) с{' '}
        <a
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={linkCls}
          onClick={(e) => e.stopPropagation()}
        >
          Политикой обработки персональных данных
        </a>
        <span className="text-[#e3282a]"> *</span>
      </Row>
      {error && <p className="text-sm text-[#e3282a] -mt-1 ml-8">{error}</p>}

      {onMarketingChange && (
        <Row id="consent_marketing" checked={marketing} onChange={onMarketingChange}>
          Хочу получать новости, акции и предложения и даю{' '}
          <button type="button" className={linkCls} onClick={open('marketing')}>
            согласие на рекламные рассылки
          </button>
        </Row>
      )}

      <LegalDocumentModal
        kind={openDoc}
        onClose={() => setOpenDoc(null)}
        onAccept={() => {
          if (openDoc === 'personal-data') onPersonalDataChange(true);
          if (openDoc === 'marketing' && onMarketingChange) onMarketingChange(true);
        }}
      />
    </div>
  );
};
